import React, { useState } from 'react';

import { faBolt, faCircleExclamation, faClipboard, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-4';

import ActivityList from './activity_list';
import ActivityModal from './modal';
import Button from 'shared/button';
import CopyOverlay from 'components/copy_overlay';
import EmptyState from 'shared/empty_state';
import Filters from './filters';
import LoadingSpinner from 'components/loading_spinner';
import { buildActivity } from './form/helpers';
import { useActivitiesState } from './hooks';
import * as filterHelpers from './filters/helpers';

import type { Activity, Features, FilterOptions, PaginationData } from './types';
import type { Paths } from './hooks';
import type { Parent } from 'bundles/crm/types';
import type { Props as EmptyStateProps } from 'shared/empty_state';

export interface Props {
  activities: Activity[];
  importerProps: any;
  isBackofficeAdmin?: boolean;
  forUserId?: number;
  currentParent?: Parent | Parent[];
  emptyStateComponent?: React.ComponentType<EmptyStateProps>;
  features?: Features;
  filters?: boolean;
  filterOptions?: FilterOptions;
  header?: React.ReactNode;
  inboxAddress?: string;
  options?: Object;
  paginate?: boolean;
  paths?: { availableTags?: string, create?: string } & Paths,
  showParents?: boolean | 'path';
  tagText?: string;
  onChange?: (activities: Activity[]) => void;
  onEmptyStateClick?: () => void;
  onPageDataChange?: (pageData: PaginationData) => void;
}

export default function Activities({
  activities: activitiesProp,
  currentParent,
  emptyStateComponent: EmptyStateComponent = EmptyState,
  features,
  filters: showFilters,
  filterOptions,
  header,
  inboxAddress,
  options,
  paginate,
  paths,
  showParents,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onChange,
  onEmptyStateClick,
  onPageDataChange,
}: Props) {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    activities,
    loading,
    pageData,
    addActivity,
    removeActivity,
    replaceActivity,
    filters,
    changeFilters,
  } = useActivitiesState({ activities: activitiesProp, paginate, paths, onChange, onPageDataChange });

  if (loading && !showFilters) {
    return <LoadingSpinner />;
  }

  const headerParts: React.ReactNode[] = [];
  if (header) {
    const headerPart = (
      <div className="d-flex align-items-center">
        <h5 className="my-3 me-2" key="header">{header}</h5>
        {inboxAddress && (
          <CopyOverlay className="text-muted" dark text={inboxAddress} tooltip="Copy inbox address for this record">
            <FontAwesomeIcon icon={faClipboard} />
          </CopyOverlay>
        )}
      </div>
    );
    headerParts.push(headerPart);
  }

  if (paths?.create) {
    headerParts.push(
      <Button.Primary
        icon={faPlus}
        key="add"
        label="New activity"
        onClick={() => setShowCreateModal(true)}
      />
    );
  }

  let content;
  const hasFilters = filterHelpers.hasFilters(filters);

  if (activities.length === 0 && !loading && (!showFilters || !hasFilters)) {
    let onClick;
    if (onEmptyStateClick) {
      onClick = onEmptyStateClick;
    } else if (paths?.create) {
      onClick = () => setShowCreateModal(true);
    }

    content = (
      <EmptyStateComponent
        bodyText="Get started by adding an activity"
        buttonIcon={faPlus}
        buttonText="Add activity"
        header="No Activities to Display"
        icon={faBolt}
        plainButton={false}
        onClick={onClick}
      />
    );
  } else {
    let activityContent;

    if (loading) {
      activityContent = <LoadingSpinner />;
    } else if (activities.length === 0) {
      activityContent = (
        <EmptyStateComponent
          header="No Activities to Display"
          icon={faCircleExclamation}
        />
      );
    } else {
      activityContent = (
        <ActivityList
          activities={activities}
          currentParent={currentParent}
          forUserId={forUserId}
          hasFilters={hasFilters}
          isBackofficeAdmin={isBackofficeAdmin}
          options={options}
          showParents={showParents}
          tagText={tagText}
          onDelete={removeActivity}
          onSave={replaceActivity}
        />
      );
    }

    content = <>
      <div className="d-flex justify-content-between align-items-center">
        {headerParts}
      </div>

      <Filters
        features={features}
        filterOptions={filterOptions}
        filters={filters}
        show={showFilters}
        onChange={changeFilters}
      />

      {activityContent}

      {!loading && pageData && pageData.totalPages > 1 &&
        <div className="d-flex justify-content-center mt-4">
          <UltimatePagination
            currentPage={pageData.currentPage}
            totalPages={pageData.totalPages}
            onChange={page => changeFilters({ page })}
          />
        </div>
      }
    </>;
  }

  return (
    <div className="mb-5">
      {content}

      <ActivityModal
        activity={buildActivity(paths?.create, { availableTagsPath: paths?.availableTags })}
        header="Add activity"
        options={options}
        show={showCreateModal}
        tagText={tagText}
        onHide={() => setShowCreateModal(false)}
        onSave={(activity) => {
          addActivity(activity);
          setShowCreateModal(false);
        }}
      />
    </div>
  );
}
